import { useState, useCallback } from 'react';
import { ActivityApiService } from '@/services/course-content.service';

export interface Lesson {
  id: string;
  title: string;
  order: number;
}

export const useModuleLessons = () => {
  const [lessonsMap, setLessonsMap] = useState<Record<string, Lesson[]>>({});
  const [loadingModules, setLoadingModules] = useState<Record<string, boolean>>({});

  const fetchLessonsForModule = useCallback(async (moduleId: string) => {
    if (lessonsMap[moduleId] || loadingModules[moduleId]) return;

    setLoadingModules(prev => ({ ...prev, [moduleId]: true }));
    try {
      const activities = await ActivityApiService.listActivitiesForTopic(parseInt(moduleId, 10));
      const lessons: Lesson[] = activities
        .sort((a, b) => a.order - b.order)
        .map(a => ({ id: String(a.id), title: a.title, order: a.order }));
      setLessonsMap(prev => ({ ...prev, [moduleId]: lessons }));
    } catch (err) {
      console.error(`Failed to fetch lessons for module ${moduleId}:`, err);
      setLessonsMap(prev => ({ ...prev, [moduleId]: [] }));
    } finally {
      setLoadingModules(prev => ({ ...prev, [moduleId]: false }));
    }
  }, [lessonsMap, loadingModules]);

  const addLesson = useCallback(async (moduleId: string): Promise<Lesson | null> => {
    try {
      const currentLessons = lessonsMap[moduleId] ?? [];
      const activity = await ActivityApiService.createActivity({
        topic_id: parseInt(moduleId, 10),
        title: '',
        order: currentLessons.length + 1,
        description: '',
        is_visible: true,
        completion_type: 'all_blocks_complete',
      });
      const newLesson: Lesson = {
        id: String(activity.id),
        title: activity.title,
        order: activity.order,
      };
      setLessonsMap(prev => ({
        ...prev,
        [moduleId]: [...(prev[moduleId] ?? []), newLesson],
      }));
      return newLesson;
    } catch (err) {
      console.error('Failed to create lesson:', err);
      return null;
    }
  }, [lessonsMap]);

  const updateLessonTitle = useCallback(async (moduleId: string, lessonId: string, title: string) => {
    setLessonsMap(prev => ({
      ...prev,
      [moduleId]: (prev[moduleId] ?? []).map(l =>
        l.id === lessonId ? { ...l, title } : l
      ),
    }));
    try {
      await ActivityApiService.updateActivity(parseInt(lessonId, 10), { title });
    } catch (err) {
      console.error('Failed to update lesson title:', err);
    }
  }, []);

  const deleteLesson = useCallback(async (moduleId: string, lessonId: string) => {
    setLessonsMap(prev => ({
      ...prev,
      [moduleId]: (prev[moduleId] ?? []).filter(l => l.id !== lessonId),
    }));
    try {
      await ActivityApiService.deleteActivity(parseInt(lessonId, 10));
    } catch (err) {
      console.error('Failed to delete lesson:', err);
    }
  }, []);

  const getLessons = useCallback((moduleId: string) => {
    return lessonsMap[moduleId] ?? [];
  }, [lessonsMap]);

  const isModuleLoading = useCallback((moduleId: string) => {
    return loadingModules[moduleId] ?? false;
  }, [loadingModules]);

  return {
    getLessons,
    isModuleLoading,
    fetchLessonsForModule,
    addLesson,
    updateLessonTitle,
    deleteLesson,
  };
};